<template>
  <AppShell>
    <!-- Back / breadcrumb -->
    <div class="flex items-center gap-3 text-sm text-ink-muted mb-5">
      <button class="inline-flex items-center gap-2 hover:text-white transition-colors" @click="goBack">
        <i class="fas fa-arrow-left"></i> Back
      </button>
      <span class="text-ink-faint">/</span>
      <button class="hover:text-white transition-colors" @click="goBack">AI Mentored Lab</button>
      <i class="fas fa-chevron-right text-[10px] text-ink-faint"></i>
      <span class="text-white font-medium truncate">{{ question.title }}</span>
    </div>

    <!-- Question header card -->
    <div class="card mb-6 p-6">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="flex items-center gap-2 mb-2">
            <span class="badge bg-brand/15 text-brand"><i class="fas fa-robot"></i> AI Mentored</span>
            <span v-if="question.isCompleted" class="badge bg-success text-black"><i class="fas fa-check"></i> Completed</span>
          </div>
          <h1 class="text-xl sm:text-2xl font-extrabold text-white mb-1">{{ question.title }}</h1>
          <p class="text-ink-muted">{{ question.description }}</p>
        </div>
      </div>
      <div v-if="question.goal" class="mt-4 flex items-start gap-3 rounded-xl border border-line bg-surface-light p-4">
        <i class="fas fa-bullseye text-brand mt-0.5"></i>
        <p class="text-sm text-ink"><span class="font-semibold text-white">Goal:</span> {{ question.goal }}</p>
      </div>
    </div>

    <!-- Editor + Bot + Chat -->
    <div class="flex flex-col lg:flex-row gap-4 lg:min-h-[62vh]">
      <!-- Code editor (IDE-style) -->
      <div class="w-full lg:w-[56%] flex flex-col h-[62vh]">
        <div class="editor-shell card overflow-hidden flex flex-col flex-1">
          <!-- Window chrome -->
          <div class="flex items-center justify-between px-4 py-2.5 bg-base-800 border-b border-line shrink-0">
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-1.5">
                <span class="h-3 w-3 rounded-full bg-brand/80"></span>
                <span class="h-3 w-3 rounded-full bg-yellow-500/80"></span>
                <span class="h-3 w-3 rounded-full bg-success/80"></span>
              </div>
              <span class="text-xs font-mono text-ink-muted flex items-center gap-1.5">
                <i class="fab fa-js text-yellow-400"></i> solution.js
              </span>
            </div>
            <span class="chip text-[11px]">JavaScript</span>
          </div>
          <!-- Editor -->
          <Codemirror v-model:value="question.source_code" :options="cmOptions" placeholder="// Write your solution here..." @change="onChange" class="flex-1 min-h-0 text-left" />
          <!-- Footer -->
          <div class="flex items-center justify-between gap-3 px-4 py-3 bg-base-800 border-t border-line shrink-0">
            <span class="text-xs text-ink-faint hidden sm:flex items-center gap-1.5">
              <i class="fas fa-circle-info"></i> Submit your code for the AI mentor to review.
            </span>
            <button :disabled="isProcessing" @click="submitSolution" class="btn-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none">
              <i class="fas fa-paper-plane"></i> {{ isProcessing ? 'Reviewing...' : 'Submit Solution' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Bot mascot (decorative, desktop only) -->
      <div class="hidden lg:flex relative lg:w-[12%] items-end justify-center">
        <div class="relative w-full aspect-square max-w-[120px]">
          <img src="@/assets/bot/base.png" alt="Bot Base" class="absolute bottom-0 left-1/2 -translate-x-1/2 z-10 w-full" />
          <img :src="botFaceUrl" alt="Bot Face" class="absolute bottom-0 left-1/2 -translate-x-1/2 z-20 w-full" />
        </div>
      </div>

      <!-- Chat panel -->
      <div class="w-full lg:w-[32%] card flex flex-col h-[62vh] overflow-hidden">
        <!-- Chat header -->
        <div class="flex items-center gap-2.5 px-4 py-3 border-b border-line bg-base-800 shrink-0">
          <div class="h-8 w-8 grid place-items-center rounded-lg bg-brand/15 text-brand"><i class="fas fa-robot"></i></div>
          <div class="leading-tight">
            <p class="text-sm font-bold text-white">AI Mentor</p>
            <p class="text-[11px] text-success flex items-center gap-1"><span class="h-1.5 w-1.5 rounded-full bg-success"></span> Online</p>
          </div>
        </div>

        <!-- Messages -->
        <div ref="chatMessagesContainer" class="chat-messages flex-1 overflow-auto px-3 py-3">
          <div v-for="(message, index) in chatMessages" :key="index" :class="messageClass(message)">
            <div class="chat-bubble">
              <div v-if="message.isUser" class="chatBox">{{ message.text || '' }}</div>
              <vue-markdown v-else class="chatBox markdown-body" :source="message.text || ''" :plugins="mdPlugins" :options="mdOptions" />
            </div>
          </div>
          <div v-if="isTyping || showStartAnimation" class="server-message">
            <div class="chat-bubble">
              <div class="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Input -->
        <div class="chat-input flex items-end gap-2 p-3 border-t border-line shrink-0">
          <textarea v-model="chatInput" :disabled="isProcessing" rows="1" class="input flex-1 resize-none max-h-28" placeholder="Ask the mentor..." @keydown="handleKeydown"></textarea>
          <button @click="sendMessage" :disabled="isProcessing || !chatInput.trim()" class="btn-primary h-[42px] w-[42px] !px-0 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none">
            <i class="fas fa-paper-plane text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  </AppShell>
</template>

<script>
// import MonacoEditor from "monaco-editor-vue3";
import AppShell from "@/components/ui-components/AppShell.vue";
import apiClient from "@/axiosConfig";
import Codemirror from "codemirror-editor-vue3";
import "codemirror/addon/display/placeholder.js";

import "codemirror/mode/javascript/javascript.js";
import "codemirror/addon/display/placeholder.js";
import "codemirror/theme/dracula.css";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

import VueMarkdown from "vue-markdown-render";
import markdownItHighlight from "markdown-it-highlightjs";
import "highlight.js/styles/github-dark.css";

toastr.options = {
    positionClass: "toast-bottom-right",
    closeButton: true,
    progressBar: true,
    timeOut: 5000,
};

export default {
    name: "AIQuestionPage",
    components: {
        AppShell,
        Codemirror,
        VueMarkdown,
    },
    data() {
        return {
            mdPlugins: [markdownItHighlight],
            mdOptions: { html: false, linkify: true, breaks: true },
            question: {
                title: "",
                description: "",
                goal: "",
                source_code: "",
                learningMaterial: "",
                isCompleted: ""
            },
            chatMessages: [],
            chatInput: "",
            options: {
                fontSize: 14,
                automaticLayout: true, // Enable automatic layout for Monaco Editor
            },
            value: null, // Initialize as null to prevent premature setting
            chatId: null, // Chat session ID
            isTyping: false,
            isProcessing: false,
            botState: "idle",
            showStartAnimation: false,
        };
    },
    computed: {
        botFaceUrl() {
            return `/assets/bot/${this.botState}.png`;
        },
    },
    methods: {
        async fetchQuestionData() {
            try {
                const id = this.$route.params.id; // Assuming the route parameter contains the ID
                const response = await apiClient.get(`/ai-mentored-lab/${id}`);
                const data = response.data;

                this.question.title = data.title;
                this.question.description = data.description;
                this.question.goal = data.goal;
                this.question.source_code = data.sourceCode;
                this.question.learningMaterial = data.learningMaterial;
                this.question.isCompleted = data.isCompleted;

                // Start conversation
                await this.startConversation(id);
            } catch (error) {
                console.error("Error fetching question data:", error.response?.data || error.message);
            }
        },
        async startConversation(id) {
            try {
                this.showStartAnimation = true;
                const response = await apiClient.post(`/ai-mentored-lab/start/${id}`);
                this.chatId = response.data.chatId;

                this.showStartAnimation = false;

                if (response.data.message) {
                    this.chatMessages.push({
                        text: "",
                        isUser: false,
                    });
                    await this.typeMessage(response.data.message);
                }

            } catch (error) {
                console.error("Error starting conversation:", error.response?.data || error.message);
                this.showStartAnimation = false;
                const msg = error.response?.data?.message
                    || "Asisten AI sedang tidak tersedia saat ini. Silakan coba lagi beberapa saat lagi.";
                this.chatMessages.push({ text: msg, isUser: false });
                this.scrollToBottom();
            }
        },
        async sendMessage() {
            const userMessage = this.chatInput.trim();
            if (!userMessage) return;

            this.chatMessages.push({
                text: userMessage,
                isUser: true,
            });
            this.scrollToBottom(); // Scroll to bottom after adding user message
            this.chatInput = "";
            this.isProcessing = true;
            this.botState = "loading";
            this.isTyping = true;

            try {
                const response = await apiClient.post("/ai-mentored-lab/send-chat", {
                    chatId: this.chatId,
                    userMessage,
                });

                this.isTyping = false;
                this.botState = "idle";
                this.isProcessing = false;

                if (response.data.message) {
                    this.chatMessages.push({
                        text: "",
                        isUser: false,
                    });
                    await this.typeMessage(response.data.message);
                    this.scrollToBottom(); // Scroll to bottom after adding server message
                }
            } catch (error) {
                console.error("Error sending chat message:", error.response?.data?.message || error.message);
                const msg = error.response?.data?.message
                    || "Asisten AI sedang tidak tersedia saat ini. Silakan coba lagi beberapa saat lagi.";
                this.chatMessages.push({ text: msg, isUser: false });
                this.scrollToBottom();
                this.isTyping = false;
                this.botState = "idle";
                this.isProcessing = false;
            }
        },
        async submitSolution() {
            this.botState = "loading";
            this.isProcessing = true;
            this.isTyping = true;
            try {
                const id = this.$route.params.id;
                const response = await apiClient.post("/ai-mentored-lab/submit", {
                    id: id,
                    code: this.question.source_code,
                });
                this.isTyping = false;
                this.chatMessages.push({
                    text: "",
                    isUser: false,
                });
                await this.typeMessage(response.data.message);
                this.scrollToBottom();

                if (response.data.success) {
                    this.botState = "happy";
                    this.question.isCompleted = true;
                    toastr.success("Solution submitted successfully!", "Success");
                } else {
                    this.botState = "sad";
                    toastr.error("Failed to submit solution", "Error");
                }

                setTimeout(() => {
                    this.botState = "idle";
                }, 3000);
                this.isProcessing = false;
            } catch (error) {
                toastr.error(error.response?.data?.message || "Failed to submit solution", "Error");
                console.error("Error submitting solution:", error.response?.data?.message || error.message);
                this.isProcessing = false;
                this.isTyping = false;
            }
        },
        async onChange() {
            // console.log(this.question.source_code);
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.chatMessagesContainer;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        },
        messageClass(message) {
            return message.isUser ? "user-message" : "server-message";
        },
        async typeMessage(message) {
            for (let i = 0; i < message.length; i++) {
                this.chatMessages[this.chatMessages.length - 1].text += message[i];
                await new Promise((resolve) => setTimeout(resolve, 5)); // 50ms per character
            }
        },
        handleKeydown(event) {
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault(); // Prevent new line
                this.sendMessage(); // Send the message
            } else if (event.key === "Enter" && event.shiftKey) {
                this.chatInput += "\n"; // Allow multiline input
            }
        },
        goBack() {
            this.$router.push(`/ai-labs`);
        },
    },
    mounted() {
        this.fetchQuestionData(); // Fetch question data after the component is mounted
    },
    setup() {
        return {
            cmOptions: {
                mode: "text/javascript", // Language mode
                theme: "dracula", // Theme
            },
        };
    }
};
</script>

<style scoped>
/* Make CodeMirror fill the editor shell */
.editor-shell :deep(.codemirror-container),
.editor-shell :deep(.CodeMirror) {
    height: 100% !important;
}

/* Nice coding font (JetBrains Mono w/ ligatures) for the editor */
.editor-shell :deep(.CodeMirror),
.editor-shell :deep(.CodeMirror pre),
.editor-shell :deep(.CodeMirror-code) {
    font-family: "JetBrains Mono", "Fira Code", "Cascadia Code", ui-monospace,
        SFMono-Regular, "SF Mono", Menlo, Consolas, monospace !important;
    font-size: 13.5px;
    line-height: 1.7;
    font-variant-ligatures: contextual;
    font-feature-settings: "liga" 1, "calt" 1;
    -webkit-font-feature-settings: "liga" 1, "calt" 1;
    letter-spacing: 0.1px;
}

/* A touch more breathing room + subtle active line */
.editor-shell :deep(.CodeMirror-gutters) {
    border-right: 1px solid #222b3b;
}
.editor-shell :deep(.CodeMirror-lines) {
    padding: 0.5rem 0;
}

/* Chat Messages Container */
.chat-messages {
    text-align: justify;
    overflow-y: auto;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

/* User Message Styling */
.user-message {
    background-image: linear-gradient(135deg, #ff3131 0%, #b21919 100%);
    color: white;
    padding: 0.7rem 0.95rem;
    border-radius: 16px 16px 4px 16px;
    align-self: flex-end;
    margin-right: 2%;
    max-width: 82%;
    font-size: 0.875rem;
    position: relative;
    word-wrap: break-word;
    /* Ensure long words break to a new line */
    word-break: break-word;
    /* For consistent breaking across all browsers */
    overflow-wrap: break-word;
    /* Handles text overflow */
}

/* Arrow for User Messages */
.user-message::after {
    content: "";
    position: absolute;
    bottom: 5px;
    right: -8px;
    border-width: 8px 0 8px 9px;
    border-style: solid;
    border-color: transparent transparent transparent #b21919;
}

/* Server Message Styling */
.server-message {
    background-color: #1b2230;
    border: 1px solid #222b3b;
    color: #e6e8ec;
    padding: 0.7rem 0.95rem;
    border-radius: 16px 16px 16px 4px;
    align-self: flex-start;
    margin-left: 2%;
    max-width: 82%;
    font-size: 0.875rem;
    position: relative;
    word-wrap: break-word;
    /* Ensure long words break to a new line */
    word-break: break-word;
    /* For consistent breaking across all browsers */
    overflow-wrap: break-word;
    /* Handles text overflow */
}

/* Arrow for Server Messages */
.server-message::after {
    content: "";
    position: absolute;
    bottom: 5px;
    left: -10px;
    border-width: 10px 10px 10px 0;
    border-style: solid;
    border-color: transparent #3c3c3c transparent transparent;
}

/* Markdown rendering for AI (bot) messages. Tailwind preflight resets list-style
   to none, so re-enable disc/decimal markers explicitly. */
.markdown-body :deep(pre){background:#0a0e17;border:1px solid #222b3b;border-radius:8px;padding:10px;margin:6px 0;overflow-x:auto;font-size:12.5px;line-height:1.45;}
.markdown-body :deep(code){font-family:"JetBrains Mono",monospace;font-size:12.5px;}
.markdown-body :deep(:not(pre) > code){background:rgba(255,49,49,.15);padding:1px 5px;border-radius:4px;}
.markdown-body :deep(p){margin:4px 0;}
.markdown-body :deep(ul){margin:4px 0;padding-left:20px;list-style:disc outside;}
.markdown-body :deep(ol){margin:4px 0;padding-left:22px;list-style:decimal outside;}
.markdown-body :deep(li){margin:2px 0;display:list-item;}
.markdown-body :deep(a){color:#ff5c5c;text-decoration:underline;}


textarea:focus {
    outline: none;
}

.typing-indicator {
    display: flex;
    gap: 5px;
    justify-content: center;
    align-items: center;
    height: 20px;
}

.typing-indicator span {
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: #c4c4c4;
    border-radius: 50%;
    animation: typing 1.5s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) {
    animation-delay: 0s;
}

.typing-indicator span:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes typing {

    0%,
    80%,
    100% {
        transform: scale(0);
    }

    40% {
        transform: scale(1);
    }
}


/* General Scrollbar Styling for Chat Messages */
.chat-messages::-webkit-scrollbar {
    width: 8px;
    /* Width of the scrollbar */
    height: 8px;
    /* Height of the horizontal scrollbar */
}

.chat-messages::-webkit-scrollbar-track {
    background: #2d3748;
    /* Matches the chat background for a subtle look */
    border-radius: 4px;
    /* Rounded corners for the track */
}

.chat-messages::-webkit-scrollbar-thumb {
    background: #4a5568;
    /* Darker gray for the handle */
    border-radius: 4px;
    /* Rounded corners for the handle */
}

.chat-messages::-webkit-scrollbar-thumb:hover {
    background: #718096;
    /* Lighter gray on hover for better visibility */
}

.chat-messages::-webkit-scrollbar-thumb:active {
    background: #a0aec0;
    /* Even lighter gray for active state */
}

.chat-messages::-webkit-scrollbar-horizontal {
    height: 6px;
    /* Slim horizontal scrollbar */
}

/* Optional fallback for Firefox */
.chat-messages {
    scrollbar-color: #4a5568 #2d3748;
    /* Thumb and track colors */
    scrollbar-width: thin;
    /* Thin scrollbar */
}

/* Scrollbar Styling for Chat Input */
textarea::-webkit-scrollbar {
    width: 8px;
    /* Width of the scrollbar */
    height: 8px;
    /* Height of the horizontal scrollbar */
}

textarea::-webkit-scrollbar-track {
    background: #2d3748;
    /* Matches the input background */
    border-radius: 4px;
    /* Rounded corners for the track */
}

textarea::-webkit-scrollbar-thumb {
    background: #4a5568;
    /* Darker gray for the handle */
    border-radius: 4px;
    /* Rounded corners for the handle */
}

textarea::-webkit-scrollbar-thumb:hover {
    background: #718096;
    /* Lighter gray on hover for better visibility */
}

textarea::-webkit-scrollbar-thumb:active {
    background: #a0aec0;
    /* Even lighter gray for active state */
}

/* Optional fallback for Firefox */
textarea {
    scrollbar-color: #4a5568 #2d3748;
    /* Thumb and track colors */
    scrollbar-width: thin;
    /* Thin scrollbar */
}
</style>
